// https://github.com/bungeeapp/captions-server/blob/main/backend/src/modules/voiceover/voiceover.schemasV2.ts

import { z } from "zod";

export const ttsVoiceType = z.enum(["clone", "public"]);
export type TTSVoiceTypeType = z.infer<typeof ttsVoiceType>;

export const ttsVoiceGender = z.enum(["male", "female"]);
export type TTSVoiceGenderType = z.infer<typeof ttsVoiceGender>;

export const ttsGenerationState = z.enum(["QUEUED", "PROCESSING", "COMPLETE", "FAILED"]);
export type TTSGenerationStateType = z.infer<typeof ttsGenerationState>;

export const listTTSModelsResponse = z.object({
  models: z.array(
    z.object({
      id: z.string(),
      enabled: z.boolean(),
      displayName: z.string(),
      provider: z.object({
        id: z.string(),
        displayName: z.string(),
        thumbnailUrl: z.string(),
      }),
      textMaxChars: z.number(),
      supportedLanguages: z.array(z.string()), // ISO 639-1 codes
    })
  ),
});
export type ListTTSModelsResponseType = z.infer<typeof listTTSModelsResponse>;
export type TTSModelListItem = ListTTSModelsResponseType["models"][number];

export const listVoicesResponse = z.object({
  voices: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      gender: ttsVoiceGender.nullable(),
      languageAccent: z
        .object({
          languageCode: z.string(),
          accent: z.string().nullable(),
        })
        .nullable(),
      colors: z.array(z.string()),
      voicePreviewUrls: z.array(
        z.object({
          languageCode: z.string(),
          url: z.string(),
        })
      ),
      type: ttsVoiceType,
      providerId: z.string(),
    })
  ),
});
export type ListVoicesResponseType = z.infer<typeof listVoicesResponse>;

export type VoiceListItem = ListVoicesResponseType["voices"][number];
export type VoiceListItemWithModelId = VoiceListItem & { modelId: string };

export const submitTTSGenerationRequest = z.object({
  text: z.string(),
  voiceId: z.string(),
  modelId: z.string(),
  caller: z.string().optional(),
  optimisticProjectId: z.string(),
});
export type SubmitTTSGenerationRequestType = z.infer<typeof submitTTSGenerationRequest>;

export const submitTTSGenerationResponse = z.object({
  operationId: z.string(),
});
export type SubmitTTSGenerationResponseType = z.infer<typeof submitTTSGenerationResponse>;

export const submitTTSRegenerationRequest = z.object({
  text: z.string(),
  voiceId: z.string(),
  modelId: z.string(),
  originalOperationId: z.string(),
  previousOperationIds: z.array(z.string()),
  optimisticProjectId: z.string(),
});
export type SubmitTTSRegenerationRequestType = z.infer<typeof submitTTSRegenerationRequest>;

export const statusTTSGenerationRequest = z.object({
  operationId: z.string(),
});
export type StatusTTSGenerationRequestType = z.infer<typeof statusTTSGenerationRequest>;

export const ttsGenerationStateEnum = z.enum(["QUEUED", "PROCESSING", "COMPLETE", "FAILED"]);
export type TTSGenerationStateEnumType = z.infer<typeof ttsGenerationStateEnum>;

const statusTTSGenerationResponseComplete = z.object({
  state: z.literal(ttsGenerationStateEnum.Values.COMPLETE),
  assetId: z.string(),
  url: z.string(),
});

const statusTTSGenerationResponseProcessing = z.object({
  state: z.literal(ttsGenerationStateEnum.Values.PROCESSING),
});

const statusTTSGenerationResponseFailed = z.object({
  state: z.literal(ttsGenerationStateEnum.Values.FAILED),
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

const statusTTSGenerationResponseQueued = z.object({
  state: z.literal(ttsGenerationStateEnum.Values.QUEUED),
});

export const statusTTSGenerationResponse = z.discriminatedUnion("state", [
  statusTTSGenerationResponseComplete,
  statusTTSGenerationResponseProcessing,
  statusTTSGenerationResponseFailed,
  statusTTSGenerationResponseQueued,
]);
export type StatusTTSGenerationResponseType = z.infer<typeof statusTTSGenerationResponse>;
